<template>
  <div class="necessity-group__container">
    <CustomCallout
      :opened="isOpen"
      :controlled="true"
      :class-main="
        `${
          isOpen ? 'inner-callout-header-open__container' : ''
        } inner-callout-header__container custom__bg-gray`
      "
      @click:open="isOpen = !isOpen"
    >
      <div class="necessity-group-header">
        <div v-if="!isEditSkills" class="necessity-group-header__text">
          <span>Fortaleza/Necesidad:</span>
          <p>
            <b>{{ value.necessityText }}</b>
            <span
              v-if="additionalNecessitiesCount > 0"
              class="necessity-count-badge"
              :title="`+${additionalNecessitiesCount} destreza(s) adicional(es) en esta Sub Área`"
            >
              +{{ additionalNecessitiesCount }}
            </span>
          </p>
          <p
            v-if="(!showStrenghtsAndNecessities || !isOpen) && value.necessityGoals[0]?.strenghtsAndNecessities?.length > 1"
            class="show-more__action"
            @click="handleShowMore"
          >
            Ver Más
          </p>
          <p
            v-if="showStrenghtsAndNecessities && isOpen && value.necessityGoals[0]?.strenghtsAndNecessities?.length > 1"
            class="show-more__action"
            @click="showStrenghtsAndNecessities = false"
          >
            Ver Menos
          </p>
        </div>

        <div v-else class="necessity-group-header__skill-edition">
          <ValidationObserver ref="skillsValidator">
            <ValidationProvider
              v-slot="{ errors }"
              :rules="skillsRequired && !editableSkills.length ? 'required' : ''"
              name="las necesidades y/o fortalezas"
            >
              <CustomMultiSelect
                label="Fortalezas / Necesidades:"
                class="mt-3"
                :error-message="errors[0]"
                :options="necessityOptions"
                :disabled="isReadOnly"
                v-model="editableSkills"
              />
            </ValidationProvider>
          </ValidationObserver>
        </div>

        <div
          v-if="isEditSkills && !isReadOnly"
          class="necessity-group-header__skills-actions"
        >
          <ActionButton
            i="fa fa-times-circle mx-2"
            class="mr-2 button-size"
            variant="outline-danger"
            @click="cancelEditSkills"
          />

          <ActionButton
            i="fa fa-save mx-2"
            class="button-size"
            variant="success"
            @click="saveKills"
          />
        </div>

        <div
          v-if="!isEditSkills && !isReadOnly"
          class="necessity-group-header__actions"
        >
          <ActionButton
            i="fa fa-pencil"
            class="mr-2"
            variant="success"
            @click="isEditSkills = true"
          />
          <!-- AG-05 -- "Meta con Asistente IA": gateado únicamente por aiAssistantEnabled
               (parámetro EnableAiAssistantPei), mismo patrón que "Objetivo con Asistente IA"
               en SubAreaGoalCallout.vue -- Sub Área y Necesidad ya están fijas en este nivel,
               así que no hace falta pedirlas de nuevo antes de convocar al agente. Versión
               compacta (Variante B del mockup aprobado por el usuario) -- ver el detalle técnico
               en el comentario de .ai-goal-trigger en GoalsManager.vue. -->
          <button
            v-if="aiAssistantEnabled"
            type="button"
            class="ai-goal-trigger mr-2"
            title="Meta con Asistente IA"
            @click="openAiGoalModal($event)"
          >
            <span class="ai-trigger-icon">
              <AiSparkIcon :size="16" variant="white" />
            </span>
            <span class="ai-trigger-label">Meta con Asistente IA</span>
          </button>
          <ActionButton
            i="fas fa-file-plus"
            variant="success"
            @click="handleAddGoalClick"
          >
            Añadir Nueva Meta
          </ActionButton>
        </div>
      </div>
      <template #edition>
        <div>
          <div
            v-if="showStrenghtsAndNecessities && value?.necessityGoals[0]?.strenghtsAndNecessities?.length > 1"
            class="necessity-strength__container"
          >
            <NecessityStrengthGoalList
              title="Todas las Fortalezas y Necesidades"
              class="px-4"
              :list="value?.necessityGoals[0]?.strenghtsAndNecessities"
              :read-only="isReadOnly"
              @delete="handleDeleteNec($event)"
            />
          </div>

          <div
            v-for="(goal, index) in value.necessityGoals"
            :key="goal.peiProgramServiceGoalId"
          >
            <SubAreaGoalCallout
              :custom-id="`sub-area-callout-${index}`"
              :ref="`sub-area-callout-${index}`"
              :value="goal"
              :index="index + 1"
              :hasError="hasError"
              :adequacy-options="adequacyOptions"
              :instrument-options="instrumentOptions"
              :skills-required="skillsRequired"
              :is-read-only="isReadOnly"
              :show-objetives-section="showObjetivesSection"
              :ai-assistant-enabled="aiAssistantEnabled"
              :auto-open-ai-objectives-goal-id="autoOpenAiObjectivesGoalId"
              :suppress-auto-open-objetive-form="effectiveSuppressAutoOpenObjetiveForm"
              :necessity-text="value.necessityText"
              :sub-area-text="subAreaText"
              @add-objetive="relayAddObjetive"
              @delete-goal="$emit('delete-goal', $event)"
              @update-goal="$emit('update-goal', $event)"
              @update-objetive="$emit('update-objetive', $event)"
              @delete-objetive="$emit('delete-objetive', $event)"
              @pending-objetive="setPendingObjetive"
              @clear-ai-chain="$emit('clear-ai-chain')"
            />
          </div>
        </div>
      </template>
    </CustomCallout>

    <div
      v-if="showAddGoal"
      :class="['add-subarea-necessity__container', { 'add-subarea-necessity__container--ai': formOpenedViaAi }]"
    >
      <GoalForm
        :ref="`newGoalForm-${customId}`"
        v-model="newGoal"
        can-cancel
        :term-options="termOptions"
        :show-classifier="false"
        :suitability-options="suitabilityOptions"
        :skills-required="skillsRequired"
        @save="saveGoalHandler"
        @cancel="handleCancelGoal(`newGoalForm-${customId}`)"
      />
    </div>

    <!-- AG-05 -- "Meta con Asistente IA": propone la Meta completa (Término/Actividad/
         Adecuacidad) para esta Sub Área + Necesidad ya seleccionadas (fuente nueva, aún
         sin construir en el backend -- ver sectionArea 'V-Metas'). Al elegir una sugerencia
         se pre-llena y auto-guarda el MISMO GoalForm de "Añadir Nueva Meta" (arriba), en vez
         de duplicar el flujo de guardado/validación. -->
    <AiAssistantModal
      :visible="aiGoalModal.visible"
      :loading="aiGoalModal.loading"
      :error="aiGoalModal.error"
      :anchor-rect="aiGoalModal.anchorRect"
      :best-practices="aiGoalModal.bestPractices"
      :can-refine="!!aiGoalModal.suggestions.length"
      refine-placeholder="Ej: prioriza destrezas de lectura, término más corto..."
      :can-give-feedback="!!(aiGoalModal.suggestions.length && aiGoalModal.aiGenerationLogId)"
      :feedback="aiGoalModal.feedback"
      :feedback-submitting="aiGoalModal.feedbackSubmitting"
      title="Metas Sugeridas por IA"
      :loading-text="aiGoalModal.loadingText"
      @close="closeAiGoalModal"
      @refine="handleRefineGoal"
      @feedback="handleFeedbackGoal"
    >
      <!-- contexto: a qué Sub Área/Necesidad corresponden las Metas sugeridas (mismo
           tratamiento liviano, sin caja, que en GoalsManager.vue/SubAreaGoalCallout.vue -- ver
           comentarios ahí). Acá value.necessityText y subAreaText ya estaban disponibles
           (encabezados "Fortaleza/Necesidad"/"Sub Área" de esta misma pantalla). -->
      <div
        v-if="value.necessityText || subAreaText"
        class="ai-goal-context__breadcrumb"
      >
        <span v-if="subAreaText">Sub Área: <strong>{{ subAreaText }}</strong></span>
        <span v-if="value.necessityText">Necesidad: <strong>{{ value.necessityText }}</strong></span>
      </div>

      <div v-if="!aiGoalModal.suggestions.length" class="ai-modal-empty">
        No se generaron metas sugeridas para esta necesidad. Intente nuevamente.
      </div>

      <div
        v-for="(suggestion, idx) in aiGoalModal.suggestions"
        :key="idx"
        class="ai-goal-suggestion"
      >
        <div class="ai-goal-suggestion__header">
          <div class="ai-goal-suggestion__header-left">
            <span class="ai-goal-suggestion__badge">
              <AiSparkIcon :size="13" variant="white" /> Meta #{{ idx + 1 }}
            </span>
            <!-- "editar antes de usar" -- ver el comentario en aiSuggestionEditMixin.js. -->
            <span v-if="aiEditWasEdited(suggestion, formatAiGoalEditableText)" class="ai-goal-suggestion__edited-tag">
              <i class="fa fa-pencil" /> Editado
            </span>
          </div>
          <!-- botón único (ícono) para Editar/Cancelar -- ver el mismo comentario en
               GoalsManager.vue (íconos de FontAwesome, ya usados en el resto de la app, en vez
               de componentes SVG propios). -->
          <button
            type="button"
            class="ai-btn-icon"
            :class="{ 'ai-btn-icon--active': aiEditIsActive(suggestion) }"
            :title="aiEditIsActive(suggestion) ? 'Cancelar edición' : 'Editar'"
            @click="handleAiGoalEditToggle(suggestion)"
          >
            <i v-if="aiEditIsActive(suggestion)" class="fas fa-times" />
            <i v-else class="fa fa-pencil" />
          </button>
        </div>
        <!-- la pastilla "Meta #N" queda sola arriba (ver header) para dar contexto
             primero; el checkbox flota (float: left) al lado de la 1ª línea del texto -- la
             sugerencia se lee primero, se selecciona después. Elegido sobre varios mockups de
             imagen que se le mostraron al usuario antes de tocar código. Solo las sugerencias
             COMPLETAS (con match real de Término/Adecuacidad contra catálogo, ver
             isAiGoalSuggestionComplete) son elegibles para inserción masiva -- las incompletas
             siguen requiriendo completarlas a mano vía "Usar esta meta", y por eso no muestran
             checkbox. -->
        <div class="ai-goal-suggestion__content">
          <CustomCheckbox
            v-if="isAiGoalSuggestionComplete(suggestion) && !aiEditIsActive(suggestion)"
            v-model="suggestion._selected"
            title="Seleccionar"
            aria-label="Seleccionar"
            class="ai-goal-suggestion__checkbox"
          />
          <!-- Solo se edita la Actividad (formatAiGoalEditableText) -- Término y Adecuacidad
               se cambian aparte, con los dropdowns de al lado/abajo, ver el mismo comentario en
               GoalsManager.vue (handleAiGoalEditToggle). -->
          <CustomDropDown
            v-if="aiEditIsActive(suggestion)"
            v-model="suggestion._aiEditTerm"
            class="ai-goal-suggestion__catalog-edit"
            label="Término:"
            :options="termOptions"
            fieldkey="id"
            fieldname="name"
            placeholder="Seleccione"
          />
          <textarea
            v-if="aiEditIsActive(suggestion)"
            v-model="suggestion._aiEditText"
            class="ai-goal-suggestion__text-edit"
            rows="3"
          />
          <CustomDropDown
            v-if="aiEditIsActive(suggestion)"
            v-model="suggestion._aiEditSuitability"
            class="ai-goal-suggestion__catalog-edit"
            label="Adecuacidad:"
            :options="suitabilityOptions"
            fieldkey="id"
            fieldname="name"
            placeholder="Seleccione"
          />
          <div v-else class="ai-goal-suggestion__text">
            {{ formatAiGoalPreview(suggestion) }}
          </div>
          <div v-if="!aiEditIsActive(suggestion)" class="ai-goal-suggestion__meta">
            <span><strong>Término:</strong> {{ aiGoalDisplayTermino(suggestion) }}</span>
            <span><strong>Adecuacidad:</strong> {{ aiGoalDisplayAdecuacidad(suggestion) }}</span>
          </div>
          <div class="ai-goal-suggestion__actions">
            <button
              type="button"
              class="ai-btn ai-btn-primary"
              @click="handleAiGoalSelected(suggestion)"
            >
              <AiSparkIcon :size="13" variant="white" /> Usar esta meta
            </button>
          </div>
        </div>
      </div>

      <!-- inserción masiva: "Añadir seleccionadas" guarda, en cadena, todas las
           sugerencias marcadas (reutilizando el mismo guardado que "Usar esta meta" individual,
           una por una) sin abrir el encadenado de "Objetivo con Asistente IA" para cada una.
           Ronda 24 -- lógica y fila extraídas a aiBulkSelectMixin.js/AiBulkSelectBar.vue,
           reusadas también por GoalsManager.vue (Metas) y SubAreaGoalCallout.vue (Objetivos). -->
      <AiBulkSelectBar
        :selectable-count="aiBulkSelectableSuggestions.length"
        :selected-count="aiBulkSelectedCount"
        :all-selected="aiBulkAllSelected"
        :disabled="aiGoalModal.loading"
        @toggle-select-all="aiBulkToggleSelectAll"
        @add-selected="aiBulkAddSelected"
      />

      <div class="ai-modal-actions">
        <button
          type="button"
          class="ai-btn ai-btn-outline"
          :disabled="aiGoalModal.loading"
          @click="requestAiGoal"
        >
          <i class="fas fa-rotate-left" /> Regenerar
        </button>
      </div>
    </AiAssistantModal>
  </div>
</template>

<script>
import store from "@/store/index";
import CustomCallout from "custom-callout";
import GoalForm from "../GoalForm.vue";
import ActionButton from "ActionButton";
import SubAreaGoalCallout from "./SubAreaGoalCallout.vue";
import NecessityStrengthGoalList from '../../NecessityStrengthGoalList';
import alert from '@/mixin/alert.mixin';
import { CustomMultiSelect, CustomCheckbox } from '@/components/common';
import CustomDropDown from '@/components/common/CustomDropDown';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import AiAssistantModal from '@/components/pei/shared/ai-assistant/AiAssistantModal.vue';
import AiSparkIcon from '@/components/pei/shared/ai-assistant/AiSparkIcon.vue';
import AiBulkSelectBar from '@/components/pei/shared/ai-assistant/AiBulkSelectBar.vue';
import aiBulkSelectMixin from '@/components/pei/shared/ai-assistant/aiBulkSelectMixin.js';
import aiSuggestionEditMixin from '@/components/pei/shared/ai-assistant/aiSuggestionEditMixin.js';
import { generateSuggestion, refineSuggestion, markSuggestionUsed, submitFeedback } from '@/services/pei/aiAssistant.js';

export default {
  name: "SubAreaNecessity",
  mixins: [alert, aiBulkSelectMixin, aiSuggestionEditMixin],
  components: {
    GoalForm,
    ActionButton,
    CustomCallout,
    CustomMultiSelect,
    CustomCheckbox,
    CustomDropDown,
    SubAreaGoalCallout,
    NecessityStrengthGoalList,
    ValidationProvider,
    ValidationObserver,
    AiAssistantModal,
    AiSparkIcon,
    AiBulkSelectBar,
  },
  props: {
    value: {
      type: Object,
      default: () => ({
        necessityGoals: [],
        necessityText: null,
      })
    },
    termOptions: {
      type: Array,
      default: () => []
    },
    suitabilityOptions: {
      type: Array,
      default: () => []
    },
    adequacyOptions: {
      type: Array,
      default: () => []
    },
    instrumentOptions: {
      type: Array,
      default: () => []
    },
    necessityOptions: {
      type: Array,
      default: () => []
    },
    hasError: {
      type: Boolean,
      default: false,
    },
    skillsRequired: {
      type: Boolean,
      default: true
    },
    isReadOnly: {
      type: Boolean,
      default: false
    },
    customId: {
      type: String,
      default: 'sub-area-nec'
    },
    showObjetivesSection: {
      type: Boolean,
      default: true
    },
    aiAssistantEnabled: {
      type: Boolean,
      default: false,
    },
    // AG-05 -- "Meta + Objetivos encadenados": id real (peiProgramServiceGoalId) de la meta
    // recién creada por "Meta con Asistente IA" -- se recibe de la respuesta del guardado, no
    // de un match de texto (ver AddProgramService.handleCreateGoal). Se enhebra hacia abajo
    // hasta SubAreaGoalCallout, igual que aiAssistantEnabled.
    autoOpenAiObjectivesGoalId: {
      type: Number,
      default: null,
    },
    // bug del panel "Añadir Objetivo" abierto solo durante alta en bloque de Metas
    // (ver el comentario largo en SubAreaGoalCallout.vue). Este viene de arriba (nivel Sub Área
    // NUEVA, GoalsManager.vue) -- se combina con el aiBulkSelectInProgress PROPIO de esta
    // instancia (nivel Sub Área EXISTENTE) en el computed effectiveSuppressAutoOpenObjetiveForm,
    // porque el alta en bloque puede estar corriendo en cualquiera de los dos niveles.
    suppressAutoOpenObjetiveForm: {
      type: Boolean,
      default: false,
    },
    // "Objetivo con Asistente IA": encabezado de contexto dentro del modal (ver
    // SubAreaGoalCallout.vue) -- viene de SubAreaContainer.vue (value.subAreaText), esta
    // instancia solo lo reenvía hacia abajo junto con su propio value.necessityText.
    subAreaText: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      isOpen: true,
      showAddGoal: false,
      // true mientras "Añadir Nueva Meta" está abierto porque lo disparó una acción de IA
      // ("Usar esta meta"/inserción masiva vía applyAiGoalSuggestion) -- controla el
      // modificador de fondo violeta suave (.add-subarea-necessity__container--ai) en el
      // template. Si se abrió con el botón regular "Añadir Nueva Meta", queda en false y el
      // formulario mantiene su estilo original (mismo criterio que GoalsManager.vue).
      formOpenedViaAi: false,
      newGoal: {
        subArea: null,
        necessity: null,
        term: null,
        activity: "",
        studentName: store.getters.SelectedStudent.firstName
      },
      showStrenghtsAndNecessities: false,
      isEditSkills: false,
      editableSkills: [],
      pendingSubAreaCalloutObjetives: [],
      // AG-05 -- "Meta con Asistente IA": true entre que se elige una sugerencia y se
      // confirma su guardado -- así saveGoalHandler sabe si debe emitir 'ai-chain-goal-created'
      // (que dispara el encadenado hacia Objetivos) o si fue un guardado manual normal.
      pendingAiGoalChain: false,
      aiGoalModal: {
        visible: false,
        loading: false,
        // ver el mismo campo en GoalsManager.vue: antes era un string fijo en el
        // template, ahora también se reutiliza (con otro texto) mientras se guarda de verdad
        // la meta elegida con "Usar esta meta" (ver handleAiGoalSelected).
        loadingText: 'Generando metas sugeridas…',
        error: '',
        anchorRect: null,
        // Array<{ termino: String, actividad: String, adecuacidad: String }>
        suggestions: [],
        aiGenerationLogId: null,
        feedback: null,
        feedbackSubmitting: false,
        bestPractices: null,
      },
    };
  },
  computed: {
    // adaptadores requeridos por aiBulkSelectMixin.js (ver el contrato
    // documentado ahí): exponen el modal/las sugerencias de ESTE componente bajo los nombres
    // fijos que el mixin espera.
    aiBulkSuggestions() {
      return this.aiGoalModal.suggestions;
    },
    aiBulkModalLoading: {
      get() { return this.aiGoalModal.loading; },
      set(value) { this.aiGoalModal.loading = value; },
    },
    aiBulkModalLoadingText: {
      get() { return this.aiGoalModal.loadingText; },
      set(value) { this.aiGoalModal.loadingText = value; },
    },
    aiBulkModalVisible: {
      get() { return this.aiGoalModal.visible; },
      set(value) { this.aiGoalModal.visible = value; },
    },
    aiBulkItemNoun() {
      return 'meta';
    },
    // combina el flag de arriba (Sub Área nueva, GoalsManager.vue) con el propio
    // aiBulkSelectInProgress de esta instancia (Sub Área existente) -- ver el comentario del
    // prop suppressAutoOpenObjetiveForm.
    effectiveSuppressAutoOpenObjetiveForm() {
      return this.suppressAutoOpenObjetiveForm || this.aiBulkSelectInProgress;
    },
    // cuántas Fortalezas/Necesidades hay además de la que ya se muestra en value.necessityText,
    // para que se note sin tener que abrir "Ver Más".
    additionalNecessitiesCount() {
      return Math.max((this.value.necessityGoals[0]?.strenghtsAndNecessities?.length || 0) - 1, 0);
    },
  },
  watch: {
    value: {
      deep: true,
      immediate: true,
      handler(newValue) {
        this.editableSkills = [...newValue?.necessityGoals[0]?.strenghtsAndNecessities];
      }
    },
    newGoal: {
      deep: true,
      handler(newValue) {
        const payload = {
          customId: this.customId,
          pending: this.hasGoalChanges(newValue)
        };

        this.$emit('pending-goal', payload);
      }
    },
  },
  methods: {
    handleAddGoalClick() {
      this.showAddGoal = true;
      this.formOpenedViaAi = false;
    },
    handleCancelGoal(ref) {
      const payload = {
        customId: ref,
        pending: false
      };

      this.showAddGoal = false;
      this.formOpenedViaAi = false;
      this.$emit('pending-goal', payload);
    },
    resetState() {
      this.newGoal = {
        subArea: null,
        term: null,
        activity: '',
        studentName: store.getters.SelectedStudent.firstName,
      };
      this.formOpenedViaAi = false;
      this.showAddGoal = false;
    },
    handleShowMore() {
      this.isOpen = true;
      this.showStrenghtsAndNecessities = true;
    },
    async handleDeleteNec(nec) {
      if (!nec || !nec.id) return;

      let confirmDeletion = await this.ShowCancelModal(
        'Atención',
        `¿Está seguro que desea eliminar la ${nec.isStrength ? 'Fortaleza' : 'Necesidad'}?`,
        'Si',
        'No'
      );

      if (!confirmDeletion) return;

      const payload = {
        necessityId: nec.id,
        isStrength: nec.isStrength,
        goalIds: this.value.necessityGoals.map(x => x.peiProgramServiceGoalId)
      };
      this.$emit('delete-goal-necessity', payload);
    },
    // AG-05 -- "Meta + Objetivos encadenados": si este guardado vino de "Usar esta meta" (no
    // de "Añadir Nueva Meta" manual), pasamos un callback `onCreated` -- AddProgramService.
    // handleCreateGoal lo invoca con la Meta real ya guardada (con su peiProgramServiceGoalId)
    // DESPUÉS del refetch, y recién ahí reenviamos ese id hacia arriba con
    // 'ai-chain-goal-created' para que GoalsManager.vue identifique la meta nueva por id (no
    // por texto) y dispare "Objetivo con Asistente IA" sobre ella.
    async saveGoalHandler(goal) {
      const anyGoal = this.value.necessityGoals[0];
      const payload = {
        subAreaId: anyGoal.subAreaId,
        strenghtsAndNecessities: anyGoal.strenghtsAndNecessities,
        description: goal.description,
        // "editar antes de usar": este payload se arma a mano (no reenvía `goal`
        // completo como sí hace GoalsManager.vue) -- sin esto, el AiGenerationLogId que
        // GoalForm.save() ya calculó bien (null si se editó, el id real si no) se perdía acá
        // y ninguna Meta creada por ESTE flujo ("Usar esta meta" dentro de una Sub Área/
        // Necesidad ya existente) quedaba auditada (ver RegisterAppliedAiField). Bug encontrado
        // al revisar por qué el especialista veía "No se generaron objetivos
        // sugeridos" después de editar una Meta -- no era la causa de eso (la generación de
        // Objetivos no depende de este campo), pero sí era una pérdida real de auditoría.
        descriptionAiGenerationLogId: goal.descriptionAiGenerationLogId,
      };

      if (this.pendingAiGoalChain) {
        this.pendingAiGoalChain = false;
        this.$emit('add-sub-area-goal', payload, (created) => {
          // en inserción masiva (aiBulkSelectInProgress, ver aiBulkSelectMixin.js) NO
          // se dispara el encadenado de "Objetivo con Asistente IA" por cada Meta insertada --
          // el especialista lo abre a mano para la que quiera, una vez cerrado el modal de
          // Metas (ver AskUserQuestion: "No encadenar").
          if (!this.aiBulkSelectInProgress) {
            this.$emit('ai-chain-goal-created', created?.peiProgramServiceGoalId ?? null);
          }
          // avisa a handleAiGoalSelected (si está esperando) que el guardado real
          // terminó (éxito o error -- ver AddProgramService.handleCreateGoal), para que pueda
          // apagar el loading del modal de IA en vez de dejarlo esperando para siempre.
          if (this.$_resolveAiGoalChainSaved) {
            this.$_resolveAiGoalChainSaved();
            this.$_resolveAiGoalChainSaved = null;
          }
        });
      } else {
        this.$emit('add-sub-area-goal', payload);
      }

      this.resetState();
    },
    // reenvía 'add-objetive' (burbujeado desde SubAreaGoalCallout.vue) preservando
    // el segundo argumento (`onCreated`) -- mismo motivo que relayAddSubAreaGoal de
    // SubAreaContainer.vue: un `$emit('add-objetive', $event)` inline solo reenvía el primer
    // argumento, y "Objetivo con Asistente IA" ahora necesita ese callback para saber cuándo
    // apagar el loading de su propio modal de IA.
    relayAddObjetive(objetive, onCreated) {
      this.$emit('add-objetive', objetive, onCreated);
    },
    cancelEditSkills() {
      this.editableSkills = [...this.value?.necessityGoals[0]?.strenghtsAndNecessities];
      this.isEditSkills = false;
    },
    async saveKills() {
      const isValid = await this.$refs.skillsValidator.validate();

      if (!isValid) return;

      const payload = {
        skills: this.editableSkills,
        goalIds: this.value?.necessityGoals.map(x => x.peiProgramServiceGoalId)
      };

      this.$emit('update-skills', payload);
      this.isEditSkills = false;
    },
    async saveGoal() {
      if (!this.showAddGoal) return true;

      return await this.$refs[`newGoalForm-${this.customId}`].save();
    },
    hasGoalChanges(newValue) {
      const hasChanges = !!newValue.subArea ||
        !!newValue.necessity ||
        !!newValue.term ||
        !!newValue.activity;

      return hasChanges;
    },
    setPendingObjetive(data) {
      this.pendingSubAreaCalloutObjetives = this.pendingSubAreaCalloutObjetives.filter(x => x != data.customId);

      if (!!data.pending) {
        this.pendingSubAreaCalloutObjetives.push(data.customId);
      }
      
      const payload = {
        customId: this.customId,
        pending: data.pending
      };

      this.$emit('pending-objetive', payload);
    },
    async savePendingObjetive() {
      if(!this.pendingSubAreaCalloutObjetives.length) return;

      const saveObjetivePromises = [];
      
      for (let index = 0; index < this.pendingSubAreaCalloutObjetives.length; index++) {
        saveObjetivePromises.push(this.$refs[this.pendingSubAreaCalloutObjetives[index]][0].saveObjetive());
      }

      const results = await Promise.all(saveObjetivePromises);
      return results.every(value => !!value);
    },
    // --- AG-05 -- "Meta con Asistente IA": propone la Meta completa (Término/Actividad/
    // Adecuacidad) para esta Sub Área + Necesidad, ya fijas en este nivel. NOTA: requiere
    // que el backend agregue una fuente/prompt nuevos (sectionArea 'V-Metas') -- hoy este
    // endpoint todavía no existe, ver checkpoint en memoria del proyecto. ---
    openAiGoalModal(event) {
      const rect = event?.currentTarget?.getBoundingClientRect();
      this.aiGoalModal.anchorRect = rect
        ? { top: rect.top, bottom: rect.bottom, left: rect.left, right: rect.right, width: rect.width, height: rect.height }
        : null;
      this.aiGoalModal.visible = true;
      this.requestAiGoal();
    },
    closeAiGoalModal() {
      this.aiGoalModal.visible = false;
    },
    async requestAiGoal() {
      const anyGoal = this.value.necessityGoals[0];
      this.aiGoalModal.loading = true;
      // por si quedó en 'Guardando la meta seleccionada…' de un "Usar esta meta"
      // previo (ver handleAiGoalSelected).
      this.aiGoalModal.loadingText = 'Generando metas sugeridas…';
      this.aiGoalModal.error = '';
      try {
        const { data } = await generateSuggestion({
          module: 'PEI',
          sectionArea: 'V-Metas',
          peiId: store.getters.SelectedPei,
          subAreaId: anyGoal.subAreaId,
          // El backend distingue Fortaleza de Necesidad por columna (NecessityId/StrengthId en
          // GoalNecessityStrength), no por el id solo -- un id de Fortaleza y uno de Necesidad
          // pueden coincidir numéricamente (son tablas distintas). Se manda el objeto completo
          // {id, isStrength}, igual que ya se hace al guardar la Meta (ver AddProgramService.vue,
          // handleCreateGoal -> strenghtsAndNecessities), en vez de una lista de ids sueltos.
          strenghtsAndNecessities: anyGoal.strenghtsAndNecessities || [],
        });

        this.applyAiGoalResult(data);
      } catch (err) {
        this.aiGoalModal.error =
          err?.response?.data ||
          'No se pudieron generar metas sugeridas. Intente nuevamente.';
      } finally {
        this.aiGoalModal.loading = false;
      }
    },
    async handleRefineGoal(instruccion) {
      if (!this.aiGoalModal.aiGenerationLogId) return;
      this.aiGoalModal.loading = true;
      this.aiGoalModal.error = '';
      try {
        const { data } = await refineSuggestion({
          aiGenerationLogId: this.aiGoalModal.aiGenerationLogId,
          teacherInstruction: instruccion,
        });

        this.applyAiGoalResult(data);
      } catch (err) {
        this.aiGoalModal.error =
          err?.response?.data ||
          'No se pudo refinar la sugerencia. Intente nuevamente.';
      } finally {
        this.aiGoalModal.loading = false;
      }
    },
    applyAiGoalResult(data) {
      this.aiGoalModal.aiGenerationLogId = data.aiGenerationLogId;
      this.aiGoalModal.feedback = null;
      this.aiGoalModal.feedbackSubmitting = false;
      this.aiGoalModal.bestPractices =
        data.bestPractices || this.aiGoalModal.bestPractices || null;

      if (data.isBlocked) {
        this.aiGoalModal.error = data.blockedReason;
        this.aiGoalModal.suggestions = [];
        return;
      }

      let parsed = {};
      try {
        parsed = JSON.parse(data.suggestion);
      } catch (e) {
        parsed = {};
      }

      // el modelo puede devolver "actividad" (la única parte de texto libre de la Meta)
      // partida en párrafos -- se aplana a texto corrido acá, ANTES de guardarla en la
      // sugerencia (ver aiFlattenSuggestionText en aiSuggestionEditMixin.js), porque
      // applyAiGoalSuggestion usa suggestion.actividad DIRECTO cuando no hubo edición.
      this.aiGoalModal.suggestions = (parsed.metas || []).map((m) => (
        { ...m, actividad: this.aiFlattenSuggestionText(m.actividad), _selected: false }
      ));
    },
    // "editar antes de usar": la parte libre de editar -- ver el mismo comentario en
    // GoalsManager.vue.
    formatAiGoalEditableText(suggestion) {
      return suggestion.actividad;
    },
    formatAiGoalPreview(suggestion) {
      const studentName = store.getters.SelectedStudent.firstName;
      return `${suggestion.termino}, ${studentName} ${suggestion.actividad}. ${suggestion.adecuacidad}.`;
    },
    // ver el mismo comentario en GoalsManager.vue.
    aiGoalDisplayTermino(suggestion) {
      return (suggestion._aiEditTerm && suggestion._aiEditTerm.name) || suggestion.termino;
    },
    // ver el mismo comentario en GoalsManager.vue.
    aiGoalDisplayAdecuacidad(suggestion) {
      return (suggestion._aiEditSuitability && suggestion._aiEditSuitability.name) || suggestion.adecuacidad;
    },
    // ver el mismo comentario en GoalsManager.vue.
    handleAiGoalEditToggle(suggestion) {
      const wasActive = this.aiEditIsActive(suggestion);
      this.aiEditToggle(suggestion, this.formatAiGoalEditableText);

      if (!wasActive) {
        const { matchedTerm, matchedSuitability } = this.matchAiGoalSuggestion(suggestion);
        this.$set(suggestion, '_aiEditTerm', matchedTerm);
        this.$set(suggestion, '_aiEditSuitability', matchedSuitability);
      } else {
        this.$set(suggestion, '_aiEditTerm', null);
        this.$set(suggestion, '_aiEditSuitability', null);
      }
    },
    // extraído de handleAiGoalSelected para reusarlo también en la inserción masiva
    // (aiBulkAddSelected, aiBulkSelectMixin.js) sin duplicar el criterio de match.
    matchAiGoalSuggestion(suggestion) {
      const normalize = (text) => (text || '').trim().toLowerCase();

      const matchedTerm = this.termOptions.find(
        (x) => normalize(x.name) === normalize(suggestion.termino)
      ) || null;
      const matchedSuitability = this.suitabilityOptions.find(
        (x) => normalize(x.name) === normalize(suggestion.adecuacidad)
      ) || null;

      return { matchedTerm, matchedSuitability };
    },
    // Una sugerencia es "completa" (auto-guardable) cuando Término y Adecuacidad matchean el
    // catálogo real y trae Actividad -- mismo criterio que ya usaba handleAiGoalSelected. Solo
    // las completas son elegibles para la casilla de selección masiva (ver aiBulkIsSelectable,
    // abajo, y aiBulkSelectMixin.js).
    isAiGoalSuggestionComplete(suggestion) {
      const { matchedTerm, matchedSuitability } = this.matchAiGoalSuggestion(suggestion);
      return !!(matchedTerm && matchedSuitability && suggestion.actividad);
    },
    // Adaptador de aiBulkSelectMixin.js -- ver el contrato documentado ahí.
    aiBulkIsSelectable(suggestion) {
      return this.isAiGoalSuggestionComplete(suggestion);
    },
    // lógica de guardado de UNA sugerencia, extraída de handleAiGoalSelected para
    // reusarla también en la inserción masiva. Reutiliza el MISMO GoalForm/newGoal de "Añadir
    // Nueva Meta" (igual que antes) -- funciona en cadena porque cada llamada espera a que el
    // guardado anterior termine del todo (incl. resetState()) antes de que la siguiente
    // sobreescriba newGoal. Devuelve true si de verdad se guardó (sugerencia completa), false
    // si solo se dejó prellenado el form para completar a mano (incompleta).
    async applyAiGoalSuggestion(suggestion) {
      const { matchedTerm, matchedSuitability } = this.matchAiGoalSuggestion(suggestion);
      // "editar antes de usar" -- ver el comentario en GoalsManager.vue-
      // >applyAiGoalSuggestion (mismo criterio, misma auditoría, mismos Término/Adecuacidad
      // protegidos vía dropdown en vez de texto libre).
      const wasEdited = this.aiEditHasEditedText(suggestion);
      const finalTerm = (wasEdited && suggestion._aiEditTerm) || matchedTerm;
      const finalSuitability = (wasEdited && suggestion._aiEditSuitability) || matchedSuitability;
      const finalTermText = finalTerm ? finalTerm.name : suggestion.termino;
      const finalAdecuacidadText = finalSuitability ? finalSuitability.name : suggestion.adecuacidad;
      const studentName = store.getters.SelectedStudent.firstName;
      const finalActivityText = wasEdited
        ? this.aiEditFinalText(suggestion, this.formatAiGoalEditableText)
        : (suggestion.actividad || '');

      this.newGoal = {
        subArea: null,
        term: finalTerm,
        activity: finalActivityText,
        suitability: finalSuitability,
        studentName,
        descriptionOverride: wasEdited
          ? `${finalTermText}, ${studentName} ${finalActivityText}. ${finalAdecuacidadText}.`
          : null,
        descriptionAiGenerationLogId: wasEdited ? null : this.aiGoalModal.aiGenerationLogId,
      };

      if (this.aiGoalModal.aiGenerationLogId) {
        markSuggestionUsed({ aiGenerationLogId: this.aiGoalModal.aiGenerationLogId }).catch(() => {});
      }

      this.showAddGoal = true;
      this.formOpenedViaAi = true;

      // Auto-guarda solo si el Término y la Adecuacidad matchearon contra el catálogo real
      // (si no hubo match exacto, el formulario queda abierto y prellenado para que el
      // especialista lo complete/corrija a mano -- mismo criterio que "Objetivo con
      // Asistente IA"). GoalForm.validate() de todas formas exige ambos como 'required'.
      if (matchedTerm && matchedSuitability && suggestion.actividad) {
        // antes esto cerraba el modal de inmediato y el guardado real (POST +
        // refetch, ver AddProgramService.handleCreateGoal) corría totalmente invisible.
        // Ahora dejamos el modal abierto EN LOADING hasta que el guardado de verdad termine.
        this.pendingAiGoalChain = true;
        this.aiGoalModal.loading = true;
        // En bulk, el loadingText por-item ("Guardando meta X de N…") ya lo puso
        // aiBulkAddSelected (aiBulkSelectMixin.js) -- no lo pisamos acá.
        if (!this.aiBulkSelectInProgress) {
          this.aiGoalModal.loadingText = 'Guardando la meta seleccionada…';
        }

        const savedPromise = new Promise((resolve) => {
          this.$_resolveAiGoalChainSaved = resolve;
        });

        await this.$nextTick();
        await this.$refs[`newGoalForm-${this.customId}`].save();
        await savedPromise;

        if (!this.aiBulkSelectInProgress) {
          this.aiGoalModal.loading = false;
        }

        return true;
      }

      return false;
    },
    async handleAiGoalSelected(suggestion) {
      await this.applyAiGoalSuggestion(suggestion);
      this.aiGoalModal.visible = false;
    },
    // Adaptador de aiBulkSelectMixin.js -- ver el contrato documentado ahí. A diferencia de
    // GoalsManager.vue, este componente no necesita aiBulkBeforeAll/aiBulkBeforeEach: Sub Área
    // y Necesidad salen de this.value.necessityGoals[0] (estable, no de newGoal), así que
    // resetState() a mitad de cadena no las pierde.
    async aiBulkSaveOne(suggestion) {
      return await this.applyAiGoalSuggestion(suggestion);
    },
    aiBulkItemLabel(count) {
      return count === 1 ? 'meta sugerida' : 'metas sugeridas';
    },
    async handleFeedbackGoal(liked) {
      const logId = this.aiGoalModal.aiGenerationLogId;
      if (!logId || this.aiGoalModal.feedbackSubmitting) return;

      const previous = this.aiGoalModal.feedback;
      this.aiGoalModal.feedback = liked ? 'up' : 'down';
      this.aiGoalModal.feedbackSubmitting = true;

      try {
        await submitFeedback({ aiGenerationLogId: logId, liked });
      } catch (err) {
        this.aiGoalModal.feedback = previous;
        this.ShowToast('', 'No se pudo registrar tu respuesta. Intente nuevamente.', 'error');
      } finally {
        this.aiGoalModal.feedbackSubmitting = false;
      }
    },
  }
};
</script>

<style>
.inner-callout-header__container {
  background-color: white;
}

.inner-callout-header-open__container {
  border-bottom: 2px solid #d9e5df;
}

.necessity-group__container {
  margin-bottom: 10px;
}

.necessity-group-header {
  display: flex;
  justify-content: space-between;
}

.necessity-group-header__skill-edition {
  width: 100%;
}

.necessity-group-header__actions {
  display: flex;
  flex-wrap: wrap;
  row-gap: 8px;
  align-items: center;
  margin-left: 10px;
  justify-content: flex-end;
}

.necessity-group-header__skills-actions {
  display: flex;
  min-width: 150px;
  padding-top: 40px;
  justify-content: center;
}

/* fondo/borde violeta claro (mismo esquema que .ai-field-highlighted en
   CustomTextArea.vue y el resto del Asistente de IA -- ver Tab3.vue), replicado acá tal
   cual está en GoalsManager.vue (.goal-new-form__container) para distinguir en cuál Sub
   Área/Necesidad se está agregando una Meta. */
.add-subarea-necessity__container {
  margin-top: 20px;
}

/* fondo/borde violeta suave (mismo tratamiento que .goal-new-form__container en
   GoalsManager.vue) -- solo cuando el formulario se abrió por una acción de IA
   (formOpenedViaAi), NUNCA con "Añadir Nueva Meta" manual, que mantiene el contenedor de
   arriba tal cual (sin fondo, solo el margin-top). */
.add-subarea-necessity__container--ai {
  background-color: #faf8ff;
  border: 1.5px solid #ba65ff;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.show-more__action {
  cursor: pointer;
  color: #2DB782;
  text-decoration: underline #2DB782;
}

.necessity-count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 10px;
  margin-left: 8px;
  border-radius: 20px;
  background-color: #eef1f0;
  color: #11291b;
  font-size: 13px;
  font-weight: bold;
  white-space: nowrap;
}

.necessity-strength__container {
  padding: 20px;
}

.custom__bg-gray {
  background-color: #f7f7f7 !important;
}

.button-size {
  height: 45px;
}

/* AG-05 -- "Meta con Asistente IA": mismo esquema violeta que "Objetivo con Asistente IA"
   (SubAreaGoalCallout.vue), solo renombrado a .ai-goal-trigger para no colisionar con esa
   clase (viven en el mismo árbol de componentes).

   Versión compacta (Variante B del mockup HTML aprobado por el usuario) -- mismo detalle
   técnico que el comentario de .ai-goal-trigger en GoalsManager.vue: solo ícono en reposo
   (36px, max-width recortado), se expande a 260px en :hover/:focus-visible mostrando el texto
   con fade-in. */
.ai-goal-trigger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 9px;
  border: none;
  border-radius: 4px;
  background: linear-gradient(114.12deg, #5c58ed 24.303%, #ba65ff 134.64%);
  color: #fff;
  font-family: 'Lato', sans-serif;
  font-weight: 500;
  font-size: 15px;
  line-height: 1.4;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  max-width: 36px;
  transition: max-width 0.28s cubic-bezier(0.4, 0, 0.2, 1),
              background 0.15s ease-in-out;
}

.ai-goal-trigger:hover,
.ai-goal-trigger:focus-visible {
  max-width: 260px;
  background: linear-gradient(114.12deg, #4a45d6 24.303%, #a94dff 134.64%);
}

.ai-goal-trigger .ai-trigger-icon {
  flex: 0 0 16px;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ai-goal-trigger .ai-trigger-label {
  opacity: 0;
  transform: translateX(-4px);
  transition: opacity 0.18s ease 0.05s, transform 0.18s ease 0.05s;
}

.ai-goal-trigger:hover .ai-trigger-label,
.ai-goal-trigger:focus-visible .ai-trigger-label {
  opacity: 1;
  transform: translateX(0);
}

</style>

<!--
  Estilos del CUERPO del AiAssistantModal (AG-05, Meta) -- en un <style scoped> aparte por el
  mismo motivo documentado en SubAreaGoalCallout.vue: el contenido pasado por slot se compila
  con el scope-id de ESTE archivo, no el de AiAssistantModal.vue.
-->
<style scoped>
.ai-modal-empty {
  color: #11291b;
  font-family: 'Lato', sans-serif;
  font-size: 14px;
}

/* tarjeta en columna: primero el header (pastilla "Meta #N" sola, da contexto), después
   el contenido (checkbox + texto + Término/Adecuacidad + botón) -- ver .ai-goal-suggestion__header
   y .ai-goal-suggestion__content más abajo. */
.ai-goal-suggestion {
  display: flex;
  flex-direction: column;
  gap: 10px;
  border: 1px solid #c1c0f8;
  border-radius: 4px;
  padding: 14px 16px;
  background-color: #f8f5ff;
}

/* contenido de la tarjeta (checkbox + texto + Término/Adecuacidad + botón), DEBAJO de
   la pastilla. Es un bloque normal (no flex): el checkbox flota (float: left, ver
   .ai-goal-suggestion__checkbox) y el texto lo rodea -- la 1ª línea del párrafo empieza al lado
   del checkbox, las líneas siguientes (y Término/Adecuacidad/botón, que llevan clear: both) caen
   a todo el ancho de la tarjeta. Reemplaza a .ai-goal-suggestion__body (columna flex) de la ronda
   anterior.

   El clearfix del checkbox flotado NO puede ser overflow: hidden (como estaba antes) -- ver
   el mismo comentario en GoalsManager.vue: recortaba en seco (sin scroll posible) la lista de
   opciones de los dropdowns de Término/Adecuacidad. Un ::after con clear: both logra el mismo
   efecto sin recortar nada. */
.ai-goal-suggestion__content::after {
  content: '';
  display: table;
  clear: both;
}

/* ver mismo bloque de comentario en GoalsManager.vue: contexto liviano, sin caja, y
   pastilla chica dentro de cada tarjeta ya existente (no compite con "Usar esta meta"). */
.ai-goal-context__breadcrumb {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 24px;
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: #4a5a53;
  margin: 0 0 4px 0;
}

.ai-goal-context__breadcrumb strong {
  color: #11291b;
}

/* fila de encabezado de cada tarjeta sugerida: la pastilla "Meta #N". */
/* el checkbox se sacó de este header -- ahora la pastilla queda sola arriba, para dar
   contexto antes del checkbox (ver .ai-goal-suggestion__content). */
.ai-goal-suggestion__header {
  display: flex;
  align-items: center;
  /* ver el mismo comentario en GoalsManager.vue: separa la pastilla/etiqueta
     "Editado" del botón ícono de Editar. */
  justify-content: space-between;
}

/* ver el mismo comentario en GoalsManager.vue. */
.ai-goal-suggestion__header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* "editar antes de usar" (ver aiSuggestionEditMixin.js) -- ver el mismo comentario en
   GoalsManager.vue. */
.ai-goal-suggestion__edited-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 4px;
  background: #eef0ef;
  color: #57685f;
  font-family: 'Lato', sans-serif;
  font-weight: 700;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

/* CustomCheckbox (@/components/common) -- mismo componente de checkbox que ya usa el resto del
   proyecto (ver ResultRow.vue, etc.) en vez del <input type="checkbox"> plano que tenía esto
   antes. El label que arma internamente CustomCheckbox vive en SU propio scope -- se estiliza
   acá con ::v-deep para que el texto "Seleccionar" siga el mismo tratamiento tipográfico que el
   resto de este modal. */
/* CustomCheckbox usa "input-group" como clase raíz (de Bootstrap, global), que trae
   width: 100% por defecto -- si no se anula, el checkbox flotado (float: left) se estira al
   ancho completo del contenedor en vez de medir solo su contenido. width: auto lo anula. El
   checkbox ahora flota a la izquierda del párrafo de texto (ver .ai-goal-suggestion__content) --
   la 1ª línea del texto queda al lado del checkbox, las líneas siguientes caen a todo el ancho
   una vez que el texto supera su altura. */
.ai-goal-suggestion__checkbox {
  float: left;
  width: auto;
  margin: 2px 10px 6px 0;
}

.ai-goal-suggestion__checkbox ::v-deep label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: #4a5a53;
  cursor: pointer;
}

/* radio 4px (no píldora/50px) para que quede rectangular, igual que los botones
   ".ai-btn" (ej. "Usar esta meta") -- pedido explícito del usuario. */
.ai-goal-suggestion__badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  padding: 6px 14px;
  border-radius: 4px;
  background: linear-gradient(104deg, #5c58ed 24.303%, #ba65ff 134.64%);
  color: #fff;
  font-family: 'Lato', sans-serif;
  font-weight: 700;
  font-size: 13px;
}

.ai-goal-suggestion__text {
  margin: 0;
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  line-height: 1.6;
  color: #11291b;
  white-space: pre-wrap;
}

/* "editar antes de usar" -- ver el mismo comentario en GoalsManager.vue. */
.ai-goal-suggestion__text-edit {
  display: block;
  width: 100%;
  margin: 0;
  padding: 8px 10px;
  border: 1px solid #a062fb;
  border-radius: 4px;
  outline: none;
  box-shadow: 0 0 0 3px rgba(92, 88, 237, 0.15);
  background: #fff;
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  line-height: 1.6;
  color: #11291b;
  resize: vertical;
}

/* Dropdowns de Término/Adecuacidad -- ver el mismo comentario en GoalsManager.vue. */
.ai-goal-suggestion__catalog-edit {
  max-width: 320px;
  margin: 8px 0;
}

/* clear: both asegura que Término/Adecuacidad quede SIEMPRE debajo del checkbox
   flotado, sin importar cuántas líneas ocupe el texto de arriba. */
.ai-goal-suggestion__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 24px;
  margin-top: 10px;
  clear: both;
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: #4a5a53;
}

.ai-goal-suggestion__actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
}

.ai-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.ai-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 4px;
  padding: 7px 16px;
  font-family: 'Lato', sans-serif;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
}

.ai-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.ai-btn-outline {
  background: #fff;
  border: 1px solid #2db782;
  color: #2db782;
}

/* botón ícono de Editar/Cancelar -- ver el mismo comentario en GoalsManager.vue. */
.ai-btn-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 4px;
  background: transparent;
  border: none;
  color: #57685f;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s ease-in-out, color 0.15s ease-in-out;
  /* tamaño del ícono FontAwesome (fa-pencil/fa-times) que va adentro -- antes lo daba el
     prop :size del componente SVG propio, ver el comentario del template. */
  font-size: 14px;
}

.ai-btn-icon:hover {
  background: #eef0ef;
}

/* Mientras se está editando (botón mostrando la "X" de Cancelar) -- mismo fondo que
   .ai-goal-suggestion__edited-tag para que se lea como "modo activo", no como error. */
.ai-btn-icon--active {
  background: #eef0ef;
  color: #11291b;
}

.ai-btn-primary {
  background: linear-gradient(104deg, #5c58ed 24.303%, #ba65ff 134.64%);
  border: 1px solid #5c58ed;
  color: #fff;
}

.ai-btn-primary:hover:not(:disabled) {
  background: linear-gradient(104deg, #4a45d6 24.303%, #a94dff 134.64%);
}
</style>
